/**
 * Задачи.
 *
 * Из беседы задачи приходят предложенными: «созвониться в понедельник»,
 * «получить прайс — обещали прислать». В список дел попадает только то, что
 * человек принял, — модель не решает за него, что ему делать. Задачу можно
 * завести и руками.
 */
import { useEffect, useState } from 'react';
import { db, enqueue, type LocalSupplier, type LocalTask } from '../db';
import { syncNow } from '../sync';
import { ulid, plural } from '@core';

export async function saveTask(t: LocalTask) {
  await db.tasks.put(t);
  await enqueue('task.upsert', { ...t }, ulid());
  void syncNow();
}

/** Принять предложенное, закрыть, вернуть в работу, отклонить. */
export async function setTaskStatus(t: LocalTask, status: LocalTask['status']) {
  await saveTask({ ...t, status, doneAt: status === 'done' ? new Date().toISOString() : undefined });
}

function localDay(d = new Date()): string {
  const shift = d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() - shift).toISOString().slice(0, 10);
}

/** Срок человеческими словами: «сегодня», «завтра», «пт 03.10», «просрочено на 2 дня». */
export function dueLabel(dueOn?: string): { text: string; late: boolean } | null {
  if (!dueOn) return null;
  const today = localDay();
  const days = Math.round((Date.parse(dueOn) - Date.parse(today)) / 86_400_000);
  if (days < 0) return { text: `просрочено на ${-days} ${plural(-days, 'день', 'дня', 'дней')}`, late: true };
  if (days === 0) return { text: 'сегодня', late: false };
  if (days === 1) return { text: 'завтра', late: false };
  const d = new Date(`${dueOn}T12:00:00`);
  return { text: d.toLocaleDateString('ru-RU', { weekday: 'short', day: '2-digit', month: '2-digit' }), late: false };
}

/** Сколько ждёт внимания: предложенные и открытые на сегодня или просроченные. */
export function useTaskBadge(): number {
  const [n, setN] = useState(0);
  useEffect(() => {
    void (async () => {
      const today = localDay();
      const all = await db.tasks.where('status').anyOf('proposed', 'open').toArray();
      setN(all.filter((t) => t.status === 'proposed' || (t.dueOn && t.dueOn <= today)).length);
    })();
  }, []);
  return n;
}

function TaskItem({ task, stand, onChange, onOpenTalk }: {
  task: LocalTask;
  stand?: LocalSupplier;
  onChange: () => void;
  onOpenTalk?: (id: string) => void;
}) {
  const due = dueLabel(task.dueOn);
  const done = task.status === 'done';
  const proposed = task.status === 'proposed';
  const sub = [stand?.companyName, due?.text].filter(Boolean).join(' · ');
  return (
    <div className="row task" data-done={done ? '1' : '0'}>
      {!proposed && (
        <button className="task-check" aria-label={done ? 'Вернуть в работу' : 'Сделано'}
                onClick={async () => { await setTaskStatus(task, done ? 'open' : 'done'); onChange(); }}>
          {done ? '✓' : ''}
        </button>
      )}
      <span className="grow">
        <span className="nm">{task.title}</span>
        {(sub || task.conversationId) && (
          <span className="sub">
            <span className={due?.late && !done ? 'task-late' : ''}>{sub}</span>
            {task.conversationId && onOpenTalk && (
              <>{sub ? ' · ' : ''}<button className="linky" onClick={() => onOpenTalk(task.conversationId!)}>из беседы</button></>
            )}
          </span>
        )}
        {/* Под текстом, а не сбоку: сбоку кнопки сжимают задачу в столбик. */}
        {proposed && (
          <span className="digest-acts">
            <button className="state-btn" onClick={async () => { await setTaskStatus(task, 'open'); onChange(); }}>Принять</button>
            <button className="state-btn" onClick={async () => { await setTaskStatus(task, 'dismissed'); onChange(); }}>Не нужно</button>
          </span>
        )}
      </span>
    </div>
  );
}

/** Предложенные задачи одной беседы — в её просмотре. */
export function TalkTasks({ conversationId }: { conversationId: string }) {
  const [tasks, setTasks] = useState<LocalTask[]>([]);
  const load = async () => setTasks(
    (await db.tasks.where('conversationId').equals(conversationId).toArray())
      .filter((t) => t.status !== 'dismissed')
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt)));
  useEffect(() => { void load(); }, [conversationId]);
  if (!tasks.length) return null;
  return (
    <>
      <div className="head"><h3>Задачи из беседы</h3><span>{tasks.length}</span></div>
      <div className="list">
        {tasks.map((t) => <TaskItem key={t.id} task={t} onChange={() => void load()} />)}
      </div>
    </>
  );
}

/** Открытые задачи одного стенда — в его карточке. */
export function SupplierTasks({ supplierId, onOpenTalk }: { supplierId: string; onOpenTalk?: (id: string) => void }) {
  const [tasks, setTasks] = useState<LocalTask[]>([]);
  const load = async () => setTasks(
    (await db.tasks.where('supplierId').equals(supplierId).toArray())
      .filter((t) => t.status === 'open' || t.status === 'proposed')
      .sort((a, b) => (a.dueOn ?? '9').localeCompare(b.dueOn ?? '9')));
  useEffect(() => { void load(); }, [supplierId]);
  if (!tasks.length) return null;
  return (
    <>
      <div className="head"><h3>Задачи</h3><span>{tasks.length}</span></div>
      <div className="list">
        {tasks.map((t) => <TaskItem key={t.id} task={t} onChange={() => void load()} onOpenTalk={onOpenTalk} />)}
      </div>
    </>
  );
}

export default function Tasks({ onBack, onOpenTalk, embedded }: {
  onBack?: () => void;
  onOpenTalk?: (id: string) => void;
  /** Внутри кокпита: своя шапка не нужна, вокруг уже есть раскладка. */
  embedded?: boolean;
}) {
  const [tasks, setTasks] = useState<LocalTask[]>([]);
  const [stands, setStands] = useState<Map<string, LocalSupplier>>(new Map());
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');
  const [showDone, setShowDone] = useState(false);

  async function load() {
    const [all, sup] = await Promise.all([db.tasks.toArray(), db.suppliers.toArray()]);
    setTasks(all);
    setStands(new Map(sup.map((s) => [s.id, s])));
  }
  useEffect(() => { void load(); }, []);

  async function add() {
    const t = title.trim();
    if (!t) return;
    await saveTask({
      id: ulid(), title: t, dueOn: due || undefined, status: 'open', source: 'manual',
      createdAt: new Date().toISOString(),
    });
    setTitle(''); setDue('');
    void load();
  }

  const proposed = tasks.filter((t) => t.status === 'proposed')
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  // Просроченное и сегодняшнее — наверху, без срока — в конце.
  const open = tasks.filter((t) => t.status === 'open')
    .sort((a, b) => (a.dueOn ?? '9999').localeCompare(b.dueOn ?? '9999') || b.createdAt.localeCompare(a.createdAt));
  const done = tasks.filter((t) => t.status === 'done')
    .sort((a, b) => (b.doneAt ?? '').localeCompare(a.doneAt ?? '')).slice(0, 30);

  const body = (
    <>
      <div className="task-add">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Новая задача"
               onKeyDown={(e) => { if (e.key === 'Enter') void add(); }} />
        <input type="date" value={due} onChange={(e) => setDue(e.target.value)} aria-label="Срок" />
        <button className="btn btn-primary btn-sm" disabled={!title.trim()} onClick={() => void add()}>Добавить</button>
      </div>

      {proposed.length > 0 && (
        <>
          <div className="head"><h3>Предложено из бесед</h3><span>{proposed.length}</span></div>
          <div className="list">
            {proposed.map((t) => (
              <TaskItem key={t.id} task={t} stand={t.supplierId ? stands.get(t.supplierId) : undefined}
                        onChange={() => void load()} onOpenTalk={onOpenTalk} />
            ))}
          </div>
        </>
      )}

      <div className="head"><h3>Сделать</h3><span>{open.length}</span></div>
      {open.length ? (
        <div className="list">
          {open.map((t) => (
            <TaskItem key={t.id} task={t} stand={t.supplierId ? stands.get(t.supplierId) : undefined}
                      onChange={() => void load()} onOpenTalk={onOpenTalk} />
          ))}
        </div>
      ) : (
        <div className="blankslate">
          {proposed.length ? 'Примите предложенное — оно появится здесь.' : 'Дел нет. Задачи из бесед появятся сами после разговора.'}
        </div>
      )}

      {done.length > 0 && (
        <>
          <div className="head">
            <h3>Сделано</h3>
            <button className="linky" onClick={() => setShowDone((v) => !v)}>{showDone ? 'скрыть' : `показать ${done.length}`}</button>
          </div>
          {showDone && (
            <div className="list">
              {done.map((t) => (
                <TaskItem key={t.id} task={t} stand={t.supplierId ? stands.get(t.supplierId) : undefined}
                          onChange={() => void load()} onOpenTalk={onOpenTalk} />
              ))}
            </div>
          )}
        </>
      )}
    </>
  );

  if (embedded) {
    return (
      <div className="desk-scroll">
        <div className="desk-head">
          <h1>Задачи</h1>
          <p>Из бесед задачи приходят предложенными — в список попадает принятое.</p>
        </div>
        <div style={{ maxWidth: 720 }}>{body}</div>
      </div>
    );
  }
  return (
    <div className="app tabbed">
      <div className="bar">
        {onBack && <button className="crumb" onClick={onBack}>‹ Назад</button>}
        <h1 className="t">Задачи</h1>
      </div>
      <div className="scroll">{body}</div>
    </div>
  );
}
